import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = ['pageRow', 'pageCard', 'buttonText', 'regenerateText', 'progressLabel', 'progressPercent', 'progressBar']

  static values = {
    websiteId: String,
    csrf: String
  }

  connect() {
    console.log('[PageGeneration] Controller connected for website:', this.websiteIdValue)

    // Subscribe to ActionCable channel
    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteGenerationChannel', website_id: this.websiteIdValue },
      {
        connected: () => this.handleConnected(),
        disconnected: () => this.handleDisconnected(),
        rejected: () => this.handleRejected(),
        received: (data) => this.handleReceived(data)
      }
    )
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleConnected() {
    console.log('[PageGeneration] ActionCable connected')
  }

  handleDisconnected() {
    console.log('[PageGeneration] ActionCable disconnected')
  }

  handleRejected() {
    console.error('[PageGeneration] ActionCable subscription rejected')
  }

  handleReceived(data) {
    console.log('[PageGeneration] Received:', data.event, data)

    switch (data.event) {
      case 'parallel_generation_started':
        this.handleGenerationStarted(data)
        break

      case 'page_generating':
        this.updatePageStatus(data.page_type, 'generating', data.page_title)
        break

      case 'page_completed':
        this.updatePageStatus(data.page_type, 'completed', data.page_title)
        break

      case 'page_failed':
        this.updatePageStatus(data.page_type, 'failed', data.page_title, data.error)
        break

      case 'generation_completed':
        // Reload page to show final state
        setTimeout(() => window.location.reload(), 1000)
        break

      case 'generation_failed':
        this.resetGenerateButton()
        break

      // Regenerate All events
      case 'regenerate_all_started':
        this.handleRegenerateAllStarted(data)
        break

      case 'page_regenerating':
        this.handlePageRegenerating(data)
        break

      case 'page_regenerated':
        this.handlePageRegenerated(data)
        break

      case 'page_regeneration_failed':
        this.handlePageRegenerationFailed(data)
        break

      case 'regenerate_all_completed':
        this.handleRegenerateAllCompleted(data)
        break
    }
  }

  // Called when form submits (Generate Site button clicked)
  startGeneration(_event) {
    // Don't prevent default - let the form submit normally
    // Just update UI immediately to show progress
    this.disableGenerateButtons()

    // Mark all pending pages as "will generate"
    this.pageRowTargets.forEach((row) => {
      const status = row.dataset.pageStatus
      if (status === 'pending' || status === null) {
        this.updateRowStatus(row, 'queued')
      }
    })

    this.pageCardTargets.forEach((card) => {
      const status = card.dataset.pageStatus
      if (status === 'pending' || status === null) {
        this.updateCardStatus(card, 'queued')
      }
    })

    // Form will submit and redirect - ActionCable will pick up on the new page
  }

  handleGenerationStarted(data) {
    console.log('[PageGeneration] Generation started, total pages:', data.total_pages)
  }

  updatePageStatus(pageType, status, pageTitle, error = null) {
    // Normalize page type for matching (e.g., "home" matches data-page-type="home")
    const normalizedType = pageType.split(':')[0] // Handle composite types like "service_detail:uuid"

    // Update table rows
    this.pageRowTargets.forEach((row) => {
      if (row.dataset.pageType === normalizedType || row.dataset.pageType === pageType) {
        this.updateRowStatus(row, status, error)
      }
    })

    // Update mobile cards
    this.pageCardTargets.forEach((card) => {
      if (card.dataset.pageType === normalizedType || card.dataset.pageType === pageType) {
        this.updateCardStatus(card, status, error)
      }
    })
  }

  updateRowStatus(row, status, error = null) {
    const statusCell = row.querySelector('td:nth-child(3)')
    if (!statusCell) return

    statusCell.innerHTML = this.getStatusBadge(status, error)
    row.dataset.pageStatus = status
  }

  updateCardStatus(card, status, error = null) {
    // Find existing status badge and replace it
    const existingBadge = card.querySelector('.badge-success, .badge-warning, .badge-error, .badge-default')
    if (existingBadge && existingBadge.textContent.includes('Generated') ||
        existingBadge && existingBadge.textContent.includes('Generating') ||
        existingBadge && existingBadge.textContent.includes('Failed') ||
        existingBadge && existingBadge.textContent.includes('Pending') ||
        existingBadge && existingBadge.textContent.includes('Queued')) {
      const newBadge = document.createElement('span')
      newBadge.innerHTML = this.getStatusBadge(status, error)
      existingBadge.replaceWith(newBadge.firstElementChild)
    }
    card.dataset.pageStatus = status
  }

  getStatusBadge(status, error = null) {
    switch (status) {
      case 'queued':
        return `
          <span class="badge badge-default inline-flex items-center">
            <span class="w-1.5 h-1.5 rounded-full mr-1.5" style="background-color: var(--text-tertiary);"></span>
            Queued
          </span>
        `
      case 'generating':
        return `
          <span class="badge badge-warning inline-flex items-center">
            <svg class="w-3 h-3 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Generating
          </span>
        `
      case 'completed':
        return `
          <span class="badge badge-success inline-flex items-center">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
            </svg>
            Generated
          </span>
        `
      case 'failed':
        return `
          <span class="badge badge-error inline-flex items-center" title="${error || 'Generation failed'}">
            <span class="w-1.5 h-1.5 rounded-full mr-1.5" style="background-color: var(--color-error);"></span>
            Failed
          </span>
        `
      default:
        return `
          <span class="badge badge-default inline-flex items-center">
            <span class="w-1.5 h-1.5 rounded-full mr-1.5" style="background-color: var(--text-tertiary);"></span>
            Pending
          </span>
        `
    }
  }

  disableGenerateButtons() {
    // Find all generate buttons and disable them
    document.querySelectorAll('#generateSiteBtn, [data-action*="startGeneration"]').forEach((btn) => {
      btn.disabled = true
      const textSpan = btn.querySelector('span') || btn
      if (textSpan) {
        textSpan.innerHTML = `
          <svg class="w-4 h-4 mr-1.5 animate-spin inline" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Generating...
        `
      }
    })
  }

  resetGenerateButton() {
    document.querySelectorAll('#generateSiteBtn, [data-action*="startGeneration"]').forEach((btn) => {
      btn.disabled = false
      const textSpan = btn.querySelector('span') || btn
      if (textSpan) {
        textSpan.textContent = 'Generate site'
      }
    })
  }

  // ==========================================
  // Regenerate All Functions
  // ==========================================

  async startRegenerateAll(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const url = btn.dataset.regenerateUrl

    if (!url) {
      console.error('[PageGeneration] No regenerate URL found')
      return
    }

    // Show loading state on button
    this.setRegenerateButtonLoading(true)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        this.setRegenerateButtonLoading(false)
        this.showToast(data.error || 'Failed to start regeneration', 'error')
        return
      }

      // Success - worker is queued, ActionCable will handle progress
      this.showToast(data.message, 'success')
    } catch (error) {
      console.error('[PageGeneration] Regenerate error:', error)
      this.setRegenerateButtonLoading(false)
      this.showToast('Failed to start regeneration', 'error')
    }
  }

  handleRegenerateAllStarted(data) {
    console.log('[PageGeneration] Regenerate all started, total pages:', data.total_pages)

    // Show progress bar
    this.showProgressBar()
    this.updateProgress(0, `Regenerating ${data.total_pages} pages...`)

    // Mark all pages as queued
    this.pageRowTargets.forEach((row) => {
      this.updateRowStatus(row, 'queued')
    })
    this.pageCardTargets.forEach((card) => {
      this.updateCardStatus(card, 'queued')
    })
  }

  handlePageRegenerating(data) {
    this.updatePageStatusById(data.page_id, 'generating', data.page_title)
    this.updateProgress(data.progress, `Regenerating: ${data.page_title}`)
  }

  handlePageRegenerated(data) {
    this.updatePageStatusById(data.page_id, 'completed', data.page_title)
    this.updateProgress(data.progress)
  }

  handlePageRegenerationFailed(data) {
    this.updatePageStatusById(data.page_id, 'failed', data.page_title, data.error)
    this.updateProgress(data.progress)
  }

  handleRegenerateAllCompleted(data) {
    console.log('[PageGeneration] Regenerate all completed:', data)

    // Update progress to 100%
    this.updateProgress(100, 'Regeneration complete!')

    // Reset button
    this.setRegenerateButtonLoading(false)

    // Show completion message
    const message = data.failed_pages > 0 ?
      `Regenerated ${data.completed_pages} pages (${data.failed_pages} failed)` :
      `Successfully regenerated ${data.completed_pages} pages`

    this.showToast(message, data.failed_pages > 0 ? 'warning' : 'success')

    // Hide progress bar after delay and reload
    setTimeout(() => {
      this.hideProgressBar()
      window.location.reload()
    }, 1500)
  }

  updatePageStatusById(pageId, status, pageTitle, error = null) {
    // Update table rows by page ID
    this.pageRowTargets.forEach((row) => {
      if (row.dataset.pageId === pageId) {
        this.updateRowStatus(row, status, error)
      }
    })

    // Update mobile cards by page ID
    this.pageCardTargets.forEach((card) => {
      if (card.dataset.pageId === pageId) {
        this.updateCardStatus(card, status, error)
      }
    })
  }

  setRegenerateButtonLoading(loading) {
    const btn = document.getElementById('regenerateAllBtn')
    if (!btn) return

    if (loading) {
      btn.disabled = true
      btn.querySelector('.regenerate-icon')?.classList.add('hidden')
      btn.querySelector('.spinner-icon')?.classList.remove('hidden')
      if (this.hasRegenerateTextTarget) {
        this.regenerateTextTarget.textContent = 'Regenerating...'
      }
    } else {
      btn.disabled = false
      btn.querySelector('.regenerate-icon')?.classList.remove('hidden')
      btn.querySelector('.spinner-icon')?.classList.add('hidden')
      if (this.hasRegenerateTextTarget) {
        this.regenerateTextTarget.textContent = 'Regenerate'
      }
    }
  }

  showProgressBar() {
    const progressEl = document.getElementById('regeneration-progress')
    if (progressEl) {
      progressEl.classList.remove('hidden')
    }
  }

  hideProgressBar() {
    const progressEl = document.getElementById('regeneration-progress')
    if (progressEl) {
      progressEl.classList.add('hidden')
    }
  }

  updateProgress(percent, label = null) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = `${percent}%`
    }
    if (label && this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = label
    }
  }

  showToast(message, type = 'success') {
    // Create toast element
    const toast = document.createElement('div')
    toast.className = `fixed bottom-4 right-4 px-4 py-3 rounded-lg text-white shadow-lg z-50 flex items-center gap-2 transition-all duration-300 transform translate-y-0`
    toast.style.backgroundColor = type === 'success' ? 'var(--color-success)' : type === 'warning' ? 'var(--color-warning)' : 'var(--color-error)'

    const icon = type === 'success' ?
      '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' :
      '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>'

    toast.innerHTML = `${icon}<span>${message}</span>`
    document.body.appendChild(toast)

    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.style.transform = 'translateY(100%)'
      toast.style.opacity = '0'
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }
}
